/**
 * AlertsPage — V2 real-time nurse call alerts page.
 *
 * 4 tabs: Emergency (real-time), Batteries (60s poll), Communication Lost (120s poll), Other (60s poll).
 * Sound notifications for new emergency alarms. Station Mode (fullscreen).
 * NotificationContext integration for real-time emergency alerts.
 *
 * Props: fullscreen — boolean for station mode
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useSound from 'use-sound';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
    getSession,
    getCampusId,
    getCampusList,
    updateSessionCampus,
    getCampusConfig,
    getCampusTzOffset,
} from '../../../services/session.js';
import { iAmHigherAuthority, sortFunc, campusToGuid, userToGuid, setSoundsMute } from '../../../libs.jsx';
import {
    fetchMaintenanceAlerts, fetchLostDevices, clearMaintenanceAlert,
} from './api/alertsApi.js';
import { fetchCampusConfig } from '../../settings/system-settings/api/systemSettingsApi.js';

import EmergencyTable from './components/EmergencyTable.jsx';
import MaintenanceTable from './components/MaintenanceTable.jsx';
import LostDevicesTable from './components/LostDevicesTable.jsx';
import AlertSyncIndicator from './components/AlertSyncIndicator.jsx';
import StationModeInfoTip from './components/StationModeInfoTip.jsx';
import emergSound from '../../../sound/emergency_tone.mp3';

import './alerts-page.css';

/** How long to wait for the opening set of alerts before sounds go live. */
const SOUND_PRIME_TIMEOUT_MS = 3000;

const AlertsPage = ({ fullscreen: fullscreenProp }) => {
    // ======================================================================
    // SESSION
    // ======================================================================
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const myRoleWeight = session?.my_role_weight || 0;
    const showAllByRole = iAmHigherAuthority(10, myRoleWeight);
    const myGroupGuids = session?.groupGuids || [];

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time emergency alerts)
    // ======================================================================
    const {
        activeAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer, resubscribeToCurrentCampus,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('emergency');
    const [fullscreen, setFullscreen] = useState(Boolean(fullscreenProp));
    const [lowBatData, setLowBatData] = useState([]);
    const [otherData, setOtherData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [clearing, setClearing] = useState(null);

    // Sort state
    const [emergSort, setEmergSort] = useState({ col: 'EventDateTime', dir: 'asc' });
    const [maintSort, setMaintSort] = useState({ col: 'EventDateTime', dir: 'asc' });
    const [lostSort, setLostSort] = useState({ col: 'last_active', dir: 'asc' });

    // High-priority alert types — set of AlertType strings flagged high priority
    // for this campus, sourced from the CampusConfig DynamoDB item
    // (HighPriorityAlertTypes). Loaded per campus; used to pin + highlight rows.
    const [highPriorityAlertTypes, setHighPriorityAlertTypes] = useState(() => new Set());

    // Sound. 4.0 campuses stay on the single emergency tone — the per-device
    // sound map is 5.0 only (CloudAlertsPage). Only *when* a sound plays is
    // shared with that page; see soundPrimedRef below.
    const [emergencySound] = useSound(emergSound, { volume: 0.5 });
    const [soundOff, setSoundOff] = useState(session?.soundsOff || false);
    const alertKeyListRef = useRef([]);
    // Alerts already active when the page opens are the starting picture, not
    // news — they must not sound. The first render pass that carries alerts
    // records their ids silently and lifts this gate. Until then nothing plays.
    const soundPrimedRef = useRef(false);

    // Open a campus with no active alerts and there is nothing to prime from, so
    // the gate above would hold forever and swallow the first real alert. Release
    // it on a timer instead. Long enough to cover the provider's initial DynamoDB
    // load, short enough that an alert landing after it is genuinely new.
    useEffect(() => {
        // A campus switch replaces the alert set wholesale, so treat it exactly
        // like arriving on the page: forget the old ids and prime again.
        alertKeyListRef.current = [];
        soundPrimedRef.current = false;
        const timer = setTimeout(() => { soundPrimedRef.current = true; }, SOUND_PRIME_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [campusId]);

    // Toast
    const { toast, showToast, dismissToast } = useToast(6000);

    // ======================================================================
    // FILTER EMERGENCY ALERTS (role-based + fullscreen)
    // ======================================================================
    const filteredEmergency = useMemo(() => {
        let filtered = showAllByRole
            ? [...(activeAlarms || [])]
            : (activeAlarms || []).filter((alarm) => {
                const alarmGroupIds = alarm.GroupIds ?? [];
                return alarmGroupIds.some((gid) => myGroupGuids?.includes(gid));
            });
        if (fullscreen) {
            filtered = filtered.filter((alarm) => !alarm?.AckDateTime);
        }
        return filtered;
    }, [activeAlarms, showAllByRole, myGroupGuids, fullscreen]);

    // ======================================================================
    // SOUND — play for new alarm IDs
    //
    // One play per batch of newly-seen alarms, and never for the alarms that
    // were already active when the page opened — arriving on the page is silent,
    // anything that lands afterwards sounds. Same timing as CloudAlertsPage;
    // the tone itself is always emergency_tone.mp3 here.
    // ======================================================================
    useEffect(() => {
        let playSound = false;
        const currentKeys = alertKeyListRef.current;
        filteredEmergency.forEach((alarm) => {
            if (!currentKeys.includes(alarm.AlarmId)) {
                if (!soundOff) playSound = true;
                currentKeys.push(alarm.AlarmId);
            }
        });
        alertKeyListRef.current = currentKeys;

        // Priming pass: the alerts that were already up when we got here have now
        // been recorded, so nothing about them is new. Play nothing and open the
        // gate. An empty page has nothing to prime from — keep waiting, and let
        // the timer above open the gate if no alerts ever show up.
        if (!soundPrimedRef.current) {
            if (filteredEmergency.length === 0) return;
            soundPrimedRef.current = true;
            return;
        }

        if (playSound) emergencySound();
    }, [filteredEmergency, soundOff, emergencySound]);

    // ======================================================================
    // HIGH-PRIORITY ALERT TYPES — from CampusConfig.HighPriorityAlertTypes.
    // Not real-time: only changes when an admin edits the setting, so we fetch
    // on mount + campus change and keep a Set of AlertType strings for O(1)
    // lookups while alarms stream in. Checked against each alarm's AlertType.
    // ======================================================================
    useEffect(() => {
        if (!campusId || !session) {
            setHighPriorityAlertTypes(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const cfg = await fetchCampusConfig(campusToGuid(session, campusId));
                if (cancelled) return;
                const types = Array.isArray(cfg?.HighPriorityAlertTypes) ? cfg.HighPriorityAlertTypes : [];
                setHighPriorityAlertTypes(new Set(types.filter(Boolean).map(String)));
            } catch (err) {
                console.error('[AlertsPage] fetchCampusConfig error:', err);
                if (!cancelled) setHighPriorityAlertTypes(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [campusId, session]);

    const isHighPriority = useCallback(
        (alarm) => highPriorityAlertTypes.has(alarm?.AlertType),
        [highPriorityAlertTypes],
    );

    // ======================================================================
    // SORTED DATA
    // ======================================================================
    // High-priority alarms always pin to the top; the active sort then orders
    // rows within the high-priority and normal groups independently.
    const sortedEmergency = useMemo(() => {
        const sorted = [...filteredEmergency];
        sorted.sort((a, b) => {
            const ap = isHighPriority(a) ? 1 : 0;
            const bp = isHighPriority(b) ? 1 : 0;
            if (ap !== bp) return bp - ap;
            return sortFunc(a, b, emergSort.col, emergSort.dir);
        });
        return sorted;
    }, [filteredEmergency, emergSort, isHighPriority]);

    const sortedLowBat = useMemo(() => {
        const sorted = [...lowBatData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lowBatData, maintSort]);

    const sortedOther = useMemo(() => {
        const sorted = [...otherData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [otherData, maintSort]);

    const sortedLost = useMemo(() => {
        const sorted = [...lostData];
        sorted.sort((a, b) => sortFunc(a, b, lostSort.col, lostSort.dir));
        return sorted;
    }, [lostData, lostSort]);

    // ======================================================================
    // POLLING — Maintenance (60s) + Lost Devices (120s)
    // ======================================================================
    const loadMaintenance = useCallback(async () => {
        if (!campusId || !session) return;
        try {
            const campusGuid = campusToGuid(session, campusId);
            const userGuid = userToGuid(session, session.user);
            const data = await fetchMaintenanceAlerts(campusGuid, userGuid, session.migrated, fullscreen ? 'station' : 'standard', {
                hybrid: session.hybrid, legacy: session.legacy, new: session.new,
            });
            const tmpLow = [];
            const tmpOther = [];
            (data || []).forEach((a) => {
                const subType = (a.AlertSubType || '').toLowerCase();
                if (['low battery', 'critbattery', 'crit battery'].includes(subType)) {
                    tmpLow.push(a);
                } else if (!['communication lost'].includes(subType)) {
                    tmpOther.push(a);
                }
            });
            setLowBatData(tmpLow);
            setOtherData(tmpOther);
        } catch (err) {
            console.error('[AlertsPage] loadMaintenance error:', err);
        }
    }, [campusId, session, fullscreen]);

    const loadLostDevices = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchLostDevices(campusId);
            setLostData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[AlertsPage] loadLostDevices error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadMaintenance(); }, [loadMaintenance]);
    useEffect(() => { loadLostDevices(); }, [loadLostDevices]);

    useEffect(() => {
        const maintTimer = setInterval(loadMaintenance, 60000);
        const lostTimer = setInterval(loadLostDevices, 120000);
        return () => { clearInterval(maintTimer); clearInterval(lostTimer); };
    }, [loadMaintenance, loadLostDevices]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud/on-premise redirect
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.href = '/emergency';
            return;
        }
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.href = '/alerts';
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
        resubscribeToCurrentCampus();
    }, [session, isCloud, resubscribeToCurrentCampus]);

    const handleSoundToggle = useCallback((e) => {
        const muted = e.target.checked;
        setSoundOff(muted);
        if (session) setSoundsMute(session, muted);
    }, [session]);

    const handleClearMaint = useCallback(async (alarmId) => {
        setClearing(alarmId);
        const campusGuid = campusToGuid(session, campusId);
        const result = await clearMaintenanceAlert(campusGuid, alarmId, session?.username);
        setClearing(null);
        if (result[0]) {
            showToast('success', 'Maintenance alarm cleared');
            loadMaintenance();
        } else {
            showToast('error', result[1] || 'Failed to clear alarm');
        }
    }, [session, campusId, showToast, loadMaintenance]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleMaintSort = useCallback((col) => {
        setMaintSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleLostSort = useCallback((col) => {
        setLostSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            {!fullscreen ? <NavigationV2 /> : null}
            <div className={fullscreen ? '' : 'nav2-main'}>
                <div className={`na-page${fullscreen ? ' na-page--fullscreen' : ''}`}>
                    {/* Toast */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Header */}
                    <div className="na-header nav2-header-offset">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Active Alerts</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <label className="na-sound-toggle">
                                <input type="checkbox" checked={soundOff} onChange={handleSoundToggle} />
                                <span>Mute Sound</span>
                            </label>
                            {!fullscreen ? (
                                <>
                                    <StationModeInfoTip>
                                        <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(true)}>
                                            <i className="fe fe-maximize" style={{ marginRight: 4 }} />Station Mode
                                        </button>
                                    </StationModeInfoTip>
                                    <div className="ps-campus-row">
                                        <label htmlFor="na-campus" className="ps-campus-label">Campus:</label>
                                        {campusList.length > 1 ? (
                                            <select id="na-campus" className="ps-campus-select" value={campusId} onChange={handleCampusChange}>
                                                {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                            </select>
                                        ) : (
                                            <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(false)}>
                                    <i className="fe fe-minimize" style={{ marginRight: 4 }} />Exit Station Mode
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tab Bar */}
                    <div className="na-tab-bar">
                        <button className={`na-tab${activeTab === 'emergency' ? ' is-active' : ''}`} onClick={() => setActiveTab('emergency')}>
                            Emergency
                            {filteredEmergency.length > 0 ? <span className="na-badge na-badge--danger">{filteredEmergency.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'batteries' ? ' is-active' : ''}`} onClick={() => setActiveTab('batteries')}>
                            Batteries
                            {lowBatData.length > 0 ? <span className="na-badge na-badge--info">{lowBatData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'lost' ? ' is-active' : ''}`} onClick={() => setActiveTab('lost')}>
                            Communication Lost
                            {lostData.length > 0 ? <span className="na-badge na-badge--info">{lostData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'other' ? ' is-active' : ''}`} onClick={() => setActiveTab('other')}>
                            Other
                            {otherData.length > 0 ? <span className="na-badge na-badge--info">{otherData.length}</span> : null}
                        </button>
                    </div>

                    {/* Content */}
                    <div className="na-content">
                        {activeTab === 'emergency' ? (
                            <EmergencyTable data={sortedEmergency} sortColumn={emergSort.col} sortOrder={emergSort.dir} onSort={handleEmergSort} tzOffset={tzOffset} highPriorityAlertTypes={highPriorityAlertTypes} />
                        ) : null}

                        {activeTab === 'batteries' ? (
                            <MaintenanceTable data={sortedLowBat} onClear={handleClearMaint} clearing={clearing} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'lost' ? (
                            <LostDevicesTable data={sortedLost} sortColumn={lostSort.col} sortOrder={lostSort.dir} onSort={handleLostSort} />
                        ) : null}

                        {activeTab === 'other' ? (
                            <MaintenanceTable data={sortedOther} onClear={handleClearMaint} clearing={clearing} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AlertsPage;
